/**
 * Tool Loader Component
 * Dynamically loads and renders tools based on their type
 */
import React from "react";
import {
  makeStyles,
  tokens,
  Text,
  Spinner,
  Card,
  Button,
} from "@fluentui/react-components";
import { ErrorCircle24Regular } from "@fluentui/react-icons";

import type { Tool } from "./types";
import { embeddedComponents } from "./embedded";

const useStyles = makeStyles({
  container: {
    height: "100%",
    width: "100%",
  },
  iframe: {
    border: "none",
    width: "100%",
    height: "100%",
    minHeight: "500px",
  },
  loading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    gap: tokens.spacingVerticalM,
  },
  error: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    padding: tokens.spacingVerticalXXL,
  },
  errorCard: {
    maxWidth: "400px",
    textAlign: "center",
    padding: tokens.spacingVerticalL,
  },
  errorIcon: {
    fontSize: "48px",
    color: tokens.colorPaletteRedForeground1,
    marginBottom: tokens.spacingVerticalM,
  },
});

interface ToolLoaderProps {
  tool: Tool;
}

/**
 * Loading fallback
 */
const ToolLoading: React.FC<{ toolName: string }> = ({ toolName }) => {
  const styles = useStyles();
  return (
    <div className={styles.loading}>
      <Spinner size="large" />
      <Text>Loading {toolName}...</Text>
    </div>
  );
};

/**
 * Error fallback
 */
const ToolError: React.FC<{ message: string; onRetry?: () => void }> = ({
  message,
  onRetry,
}) => {
  const styles = useStyles();
  return (
    <div className={styles.error}>
      <Card className={styles.errorCard}>
        <ErrorCircle24Regular className={styles.errorIcon} />
        <Text size={500} weight="semibold" block>
          Failed to Load Tool
        </Text>
        <Text style={{ marginTop: "8px", marginBottom: "16px" }}>{message}</Text>
        {onRetry && (
          <Button appearance="primary" onClick={onRetry}>
            Try Again
          </Button>
        )}
      </Card>
    </div>
  );
};

/**
 * IFrame Tool Renderer
 */
const IFrameTool: React.FC<{ url: string; title: string }> = ({ url, title }) => {
  const styles = useStyles();
  return (
    <iframe
      src={url}
      title={title}
      className={styles.iframe}
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
    />
  );
};

/**
 * Embedded Tool Renderer
 * Renders embedded React components from the registry
 */
const EmbeddedTool: React.FC<{ tool: Tool }> = ({ tool }) => {
  // Look up component by tool ID in the registry
  const Component = embeddedComponents[tool.id];

  if (!Component) {
    return <ToolError message={`Component for "${tool.id}" not found in registry`} />;
  }

  return <Component />;
};

/**
 * API Tool Renderer
 * Renders a generic interface for API-based tools
 */
const ApiTool: React.FC<{ tool: Tool }> = ({ tool }) => {
  const styles = useStyles();
  
  return (
    <div className={styles.container}>
      <Text size={600} weight="semibold" block style={{ marginBottom: "16px" }}>
        {tool.name}
      </Text>
      <Text>{tool.description}</Text>
      <Text style={{ marginTop: "16px", color: "#666" }}>
        API Endpoint: {tool.endpoint}
      </Text>
      {/* API tools would typically render a custom UI here */}
    </div>
  );
};

/**
 * External Link Tool
 */
const ExternalTool: React.FC<{ tool: Tool }> = ({ tool }) => {
  const styles = useStyles();
  
  React.useEffect(() => {
    if (tool.externalUrl) {
      window.open(tool.externalUrl, "_blank", "noopener,noreferrer");
    }
  }, [tool.externalUrl]);

  return (
    <div className={styles.loading}>
      <Text>Opening {tool.name} in a new tab...</Text>
      <Button
        appearance="primary"
        onClick={() => window.open(tool.externalUrl, "_blank", "noopener,noreferrer")}
      >
        Open Again
      </Button>
    </div>
  );
};

/**
 * Main Tool Loader Component
 */
export const ToolLoader: React.FC<ToolLoaderProps> = ({ tool }) => {
  const styles = useStyles();

  switch (tool.type) {
    case "iframe":
      if (!tool.url) {
        return <ToolError message="Tool URL is not configured" />;
      }
      return <IFrameTool url={tool.url} title={tool.name} />;

    case "embedded":
      return <EmbeddedTool tool={tool} />;

    case "api":
      return <ApiTool tool={tool} />;

    case "external":
      return <ExternalTool tool={tool} />;

    default:
      return <ToolError message={`Unknown tool type: ${tool.type}`} />;
  }
};

export default ToolLoader;
